'use client'

/**
 * Auth context — Better Auth (Convex) backed.
 *
 * Rewritten for the Convex migration (docs/CONVEX_MIGRATION_PLAN.md, fresh
 * start): sessions come from Better Auth via the first-party /api/auth/*
 * routes, and the app profile comes from the reactive Convex `auth.me`
 * query. The public interface is preserved so existing consumers keep
 * working; legacy-only concepts (beta login, super-admin email) are gone.
 */

import { createContext, useContext } from 'react'
import { useQuery } from 'convex/react'
import { api } from 'socialloop-convex/api'
import { authClient } from '@/lib/convex/auth-client'

interface UserProfile {
  id: string
  email: string
  full_name: string
  avatar_url?: string
  bio?: string
  company?: string
  job_title?: string
  location?: string
  website?: string
  linkedin_url?: string
  twitter_url?: string
  github_url?: string
  created_at: string
  authType?: 'beta' | 'main' | 'super_admin'
}

interface AuthContextType {
  user: any | null
  profile: UserProfile | null
  loading: boolean
  signUp: (
    email: string,
    password: string,
    fullName: string
  ) => Promise<{ success: boolean; error?: string; message?: string; requiresVerification?: boolean }>
  signIn: (email: string, password: string, isBeta?: boolean) => Promise<{ success: boolean; error?: string }>
  signInWithGoogle: (callbackURL?: string) => Promise<{ success: boolean; error?: string }>
  signOut: () => Promise<void>
  forgotPassword: (email: string) => Promise<{ success: boolean; error?: string }>
  resetPassword: (password: string) => Promise<{ success: boolean; error?: string }>
  refreshProfile: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const session = authClient.useSession()
  // Reactive app profile (users table row mirrored from Better Auth)
  const me = useQuery(api.auth.me, session.data ? {} : 'skip')

  const loading = session.isPending || (!!session.data && me === undefined)

  const profile: UserProfile | null = me
    ? {
        id: me._id,
        email: me.email,
        full_name: me.fullName,
        avatar_url: me.avatarUrl,
        bio: me.bio,
        company: me.company,
        job_title: me.jobTitle,
        location: me.location,
        website: me.website,
        linkedin_url: me.linkedinUrl,
        twitter_url: me.twitterUrl,
        github_url: me.githubUrl,
        created_at: new Date(me._creationTime).toISOString(),
        authType: me.role === 'SUPER_ADMIN' ? 'super_admin' : 'main',
      }
    : null

  const signUp = async (email: string, password: string, fullName: string) => {
    const { data, error } = await authClient.signUp.email({ email, password, name: fullName })
    if (error) return { success: false, error: error.message ?? 'Sign up failed' }
    // If Better Auth is configured to require email verification it does not
    // return a session token on signup — surface that so the form can route
    // to /signup/success instead of the (protected) dashboard.
    return { success: true, requiresVerification: !data?.token }
  }

  const signIn = async (email: string, password: string) => {
    const { error } = await authClient.signIn.email({ email, password })
    if (error) return { success: false, error: error.message ?? 'Sign in failed' }
    return { success: true }
  }

  const signInWithGoogle = async (callbackURL: string = '/dashboard') => {
    try {
      const { error } = await authClient.signIn.social({
        provider: 'google',
        callbackURL,
      })
      if (error) return { success: false, error: error.message ?? 'Google sign-in failed' }
      return { success: true }
    } catch (e) {
      return { success: false, error: e instanceof Error ? e.message : 'Google sign-in failed' }
    }
  }

  const signOut = async () => {
    await authClient.signOut()
  }

  const forgotPassword = async (email: string) => {
    const { error } = await authClient.requestPasswordReset({
      email,
      redirectTo: '/reset-password',
    })
    if (error) return { success: false, error: error.message ?? 'Request failed' }
    return { success: true }
  }

  const resetPassword = async (password: string) => {
    const token =
      typeof window !== 'undefined'
        ? new URLSearchParams(window.location.search).get('token')
        : null
    if (!token) return { success: false, error: 'Missing reset token' }
    const { error } = await authClient.resetPassword({ newPassword: password, token })
    if (error) return { success: false, error: error.message ?? 'Reset failed' }
    return { success: true }
  }

  // Profile is a reactive Convex query — always fresh; kept for interface compat.
  const refreshProfile = async () => {}

  return (
    <AuthContext.Provider
      value={{
        user: session.data?.user ?? null,
        profile,
        loading,
        signUp,
        signIn,
        signInWithGoogle,
        signOut,
        forgotPassword,
        resetPassword,
        refreshProfile,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
